import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_LINKS, BRAND, SERVICE_CATEGORIES } from "../data/site";
import SocialLinks from "./SocialLinks";

const LOGO = "/assets/ats-logo.png";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname, location.hash]);

  const handleNav = (to) => (e) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      const scroll = () => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
      };
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(scroll, 500);
      } else {
        scroll();
      }
      setOpen(false);
    }
  };

  const goService = (id) => (e) => {
    e.preventDefault();
    setServicesOpen(false);
    setOpen(false);
    if (location.pathname === "/services") {
      const el = document.getElementById(id);
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
      else el?.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/services#${id}`);
    } else {
      navigate(`/services#${id}`);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ${
        scrolled ? "backdrop-blur-xl bg-navy-deep/85 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src={LOGO} alt={`${BRAND.name} logo`} className="h-14 w-14 lg:h-16 lg:w-16 rounded-full object-contain shadow-lg shadow-black/30" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) =>
            l.label === "Services" ? (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={l.to}
                  data-testid="nav-link-services"
                  aria-haspopup="true"
                  aria-expanded={servicesOpen}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-full transition-colors duration-200 hover:bg-white/5 inline-flex items-center gap-1"
                >
                  {l.label} <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      data-testid="nav-services-menu"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[560px]"
                    >
                      <div className="rounded-2xl border border-white/10 bg-navy-deep/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-3 grid grid-cols-2 gap-1">
                        {SERVICE_CATEGORIES.map((c, i) => {
                          const CIcon = c.icon;
                          return (
                            <a
                              key={c.id}
                              href={`/services#${c.id}`}
                              onClick={goService(c.id)}
                              data-testid={`nav-services-item-${c.id}`}
                              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors"
                            >
                              <span className="w-9 h-9 rounded-lg bg-brand-orange/15 grid place-items-center shrink-0 group-hover:bg-brand-orange transition-colors">
                                <CIcon size={16} className="text-brand-orange group-hover:text-white transition-colors" />
                              </span>
                              <span className="min-w-0">
                                <span className="block font-mono text-[10px] text-slate-500 tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                                <span className="block text-sm text-slate-200 group-hover:text-white leading-snug">{c.title}</span>
                              </span>
                            </a>
                          );
                        })}
                        <Link to="/services" data-testid="nav-services-all" className="col-span-2 mt-1 flex items-center justify-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm font-medium text-brand-orange hover:bg-white/5 transition-colors">
                          View all services <ArrowUpRight size={14} />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                onClick={handleNav(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-full transition-colors duration-200 hover:bg-white/5"
              >
                {l.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="nav-cta" className="btn-primary">
            Get a Free Consultation <ArrowUpRight size={16} />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-navy-deep/95 border-b border-white/10 max-h-[calc(100svh-5rem)] overflow-y-auto"
          >
            <div className="container-x py-6 flex flex-col gap-2">
              {NAV_LINKS.map((l) =>
                l.label === "Services" ? (
                  <div key={l.label}>
                    <div className="flex items-center">
                      <Link
                        to={l.to}
                        data-testid="nav-mobile-link-services"
                        className="flex-1 px-4 py-3 text-base font-medium text-slate-200 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                      >
                        {l.label}
                      </Link>
                      <button
                        data-testid="nav-mobile-services-toggle"
                        aria-label="Toggle services list"
                        aria-expanded={mobileServicesOpen}
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        className="p-3 text-slate-300 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                      >
                        <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 pb-2 flex flex-col gap-1">
                            {SERVICE_CATEGORIES.map((c) => {
                              const CIcon = c.icon;
                              return (
                                <a
                                  key={c.id}
                                  href={`/services#${c.id}`}
                                  onClick={goService(c.id)}
                                  data-testid={`nav-mobile-services-item-${c.id}`}
                                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                                >
                                  <CIcon size={15} className="text-brand-orange shrink-0" /> {c.title}
                                </a>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={handleNav(l.to)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="px-4 py-3 text-base font-medium text-slate-200 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
                  >
                    {l.label}
                  </Link>
                )
              )}
              <Link to="/contact" data-testid="nav-mobile-cta" className="btn-primary mt-3">
                Get a Free Consultation <ArrowUpRight size={16} />
              </Link>
              <div className="mt-5 pt-5 border-t border-white/10 px-4">
                <p className="font-mono text-[10px] tracking-wider uppercase text-slate-500 mb-3">Follow {BRAND.short}</p>
                <SocialLinks prefix="nav-mobile" size="sm" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
